import React, { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProducts, setFilters, setPage } from '../store/slices/productsSlice';
import ProductCard from '../components/products/ProductCard';
import Pagination from '../components/common/Pagination';
import SEO from '../components/SEO/SEO';
import { fetchSEO } from '../store/slices/seoSlice';

const CategoryPage = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { items: products, pagination, loading } = useSelector((state) => state.products);
  const { items: categories } = useSelector((state) => state.categories);

  const category = categories.find((cat) => cat._id === id);
  const selectedSubcategory = searchParams.get('subcategory') || '';

  useEffect(() => {
    const page = parseInt(searchParams.get('page') || '1');
    const subcategory = searchParams.get('subcategory') || null;
    dispatch(setFilters({ category: id, subcategory }));
    dispatch(setPage(page));
    dispatch(fetchSEO('category'));
  }, [id, searchParams, dispatch]);

  useEffect(() => {
    const subcategory = searchParams.get('subcategory') || null;
    dispatch(fetchProducts({
      page: pagination.currentPage,
      limit: pagination.itemsPerPage,
      category: id,
      subcategory: subcategory,
    }));
  }, [dispatch, pagination.currentPage, id, searchParams]);

  const handlePageChange = (page) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  const handleSubcategoryClick = (subcategoryId) => {
    const params = new URLSearchParams(searchParams);
    if (subcategoryId) {
      params.set('subcategory', subcategoryId);
    } else {
      params.delete('subcategory');
    }
    params.set('page', '1'); // Reset to first page when changing subcategory
    setSearchParams(params);
  };

  return (
    <>
      {category && (
        <SEO
          page="category"
          title={`${category.name} - Premium Art Frames Collection`}
          description={category.description || `Browse our ${category.name} collection. Premium handcrafted art frames and spiritual decor.`}
          keywords={`${category.name}, art frames, spiritual decor, ${category.name} collection`}
          url={`/category/${category._id}`}
        />
      )}
      {/* Category Hero Banner - Luxe Style */}
      {category && (
        <section className="py-24 lg:py-32 bg-gray-50">
          <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
            <p className="text-xs font-sans font-medium uppercase tracking-[0.3em] text-primary mb-6">
              Our Collections
            </p>
            <h1 className="font-serif text-5xl md:text-6xl font-light text-gray-900 mb-6">
              {category.name}
            </h1>
            {category.description && (
              <p className="mt-6 text-gray-600 max-w-2xl mx-auto leading-relaxed text-lg">
                {category.description}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Subcategories Section */}
      {category && category.subcategories && category.subcategories.length > 0 && (
        <section className="py-16 lg:py-24 bg-white">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-6 md:gap-8">
                  {/* All Products Card */}
                  <button
                    onClick={() => handleSubcategoryClick(null)}
                    className={`flex flex-col items-center group transition-all duration-300 ${
                      !selectedSubcategory
                        ? 'ring-2 ring-primary ring-offset-2 rounded-full'
                        : ''
                    }`}
                  >
                    <div className={`relative w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden bg-gradient-to-br from-primary to-primary-dark shadow-md hover:shadow-xl transition-all duration-300 ${
                      !selectedSubcategory ? 'ring-2 ring-primary' : ''
                    }`}>
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-white text-2xl md:text-3xl font-bold">All</span>
                      </div>
                    </div>
                    <div className="mt-3 text-center">
                      <h3 className={`font-semibold text-sm transition ${
                        !selectedSubcategory
                          ? 'text-primary'
                          : 'text-gray-700 group-hover:text-primary'
                      }`}>
                        All Products
                      </h3>
                    </div>
                  </button>
                  
                  {/* Subcategory Cards */}
                  {category.subcategories.map((subcat) => (
                    <button
                      key={subcat._id}
                      onClick={() => handleSubcategoryClick(subcat._id)}
                      className={`flex flex-col items-center group transition-all duration-300 ${
                        selectedSubcategory === subcat._id
                          ? 'ring-2 ring-primary ring-offset-2 rounded-full'
                          : ''
                      }`}
                    >
                      <div className={`relative w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden bg-gray-100 shadow-md hover:shadow-xl transition-all duration-300 ${
                        selectedSubcategory === subcat._id ? 'ring-2 ring-primary' : ''
                      }`}>
                        {subcat.image ? (
                          <img
                            src={subcat.image}
                            alt={subcat.name}
                            className="w-full h-full object-cover image-zoom"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary to-primary-dark text-white text-xl md:text-2xl font-bold">' + (subcat.name ? subcat.name.charAt(0).toUpperCase() : '') + '</div>';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary to-primary-dark text-white text-xl md:text-2xl font-bold">
                            {subcat.name ? subcat.name.charAt(0).toUpperCase() : ''}
                          </div>
                        )}
                      </div>
                      <div className="mt-3 text-center max-w-[100px]">
                        <h3 className={`font-semibold text-sm transition line-clamp-2 ${
                          selectedSubcategory === subcat._id
                            ? 'text-primary'
                            : 'text-gray-700 group-hover:text-primary'
                        }`}>
                          {subcat.name}
                        </h3>
                      </div>
                    </button>
                  ))}
            </div>
          </div>
        </section>
      )}

      {/* Products Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow p-4 animate-pulse">
              <div className="w-full h-64 bg-gray-200 rounded mb-4" />
              <div className="h-4 bg-gray-200 rounded mb-2" />
              <div className="h-4 bg-gray-200 rounded w-2/3" />
            </div>
          ))}
        </div>
      ) : products.length > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mb-12">
            {products.map((product) => (
              <ProductCard key={product._id} product={product} />
            ))}
          </div>
          <div className="flex justify-center">
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">No products found in this category</p>
        </div>
      )}
        </div>
      </section>
    </>
  );
};

export default CategoryPage;

